import React, { type ReactNode, useEffect } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import SingleColumnTemplate from '@/components/layouts/SingleColumnTemplate';
import Jumbotron from '@/components/ui/box/Jumbotron';
import JumbotronDescription from '@/components/ui/box/JumbotronDescription.tsx';
import JumbotronPanel from '@/components/ui/box/JumbotronPanel';
import Loading from '@/components/ui/loading/Loading';
import AccountRegistrationVerifyError from '@/features/registration/components/AccountRegistrationVerifyError';
import useAuthentication from '@/hooks/common/useAuthentication';
import useReactQuery from '@/hooks/common/useReactQuery';
import { type RegistrationVerifyResponse } from '@/interfaces/registration';

import CSSModule from './AccountRegistrationVerifyPage.module.scss';

const AccountRegistrationVerifyPage: React.FC = () => {
  const { useQuery } = useReactQuery();
  const [searchParams, _] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const { login } = useAuthentication();
  const { data, isPending, isSuccess, error } =
    useQuery<RegistrationVerifyResponse>({
      queryKey: ['registration_complete', token],
      url: `/api/school/parents_v3/email_registration_tokens/${token}/verify`,
      enabled: token !== '',
      retry: false,
    });

  useEffect(() => {
    if (isSuccess && data) {
      login(data.access_token);
      navigate('/registration/complete', { replace: true });
    }
  }, [isSuccess, data, login, navigate]);

  const contents: ReactNode = React.useMemo(() => {
    if (isPending) {
      return (
        <JumbotronPanel panelHeaderTitle="本登録完了">
          <JumbotronDescription>
            <Loading />
          </JumbotronDescription>
        </JumbotronPanel>
      );
    }
    if (error) {
      return <AccountRegistrationVerifyError error={error} />;
    }
  }, [isPending, error]);

  return (
    <SingleColumnTemplate>
      <div className={CSSModule.AccountRegistrationVerify}>
        <Jumbotron>{contents}</Jumbotron>
      </div>
    </SingleColumnTemplate>
  );
};

export default AccountRegistrationVerifyPage;
